// apps/web/src/features/admin/form-config/rules/dependencyOrderCheck.ts
//
// R-43 (2026-05-14) — Cross-trigger dependency-order check.
//
// The previous check lived inside `RulesTab.tsx` and ran inside the
// dnd-kit `SortableContext` for a single `(stage, trigger)` pair. That
// meant the canonical stale-values pattern — a `calculate` rule on
// `on-change` followed by a `validate` rule on `on-validate` that
// consumes the calculated field — was NEVER flagged because the two
// rules sit in different `SortableContext`s.
//
// This module lifts the check out of the per-trigger context and into a
// stage-wide scan. Within a stage, we walk rules in their canonical
// runtime execution order (lowest trigger index → highest, ties broken
// by `displayOrder`):
//
//   on-step-enter → on-change → on-validate → on-export → on-schedule
//
// (Source: `TRIGGER_GROUPS` in `RulesTab.tsx`, which is itself ordered
//  to match runtime trigger firing.)
//
// Two patterns are flagged for a given fieldRef:
//
//   1. SAME-TRIGGER consumer-before-transform — a consumer rule
//      (validate, crossField) with a lower `displayOrder` than a
//      transform rule (substitute, format, calculate, copy) inside the
//      same trigger. This is the legacy case the pre-R-43 checker
//      already caught after a drag-reorder.
//
//   2. CROSS-TRIGGER transform-before-consumer — a transform rule on
//      an EARLIER trigger followed by a consumer on a LATER trigger
//      for the same field. Even though runtime execution order keeps
//      the transform first, the rules live in different triggers and
//      cross-trigger ordering is fragile: validation can revalidate on
//      load before on-change has settled, on-change is debounced while
//      on-validate isn't, etc. Surfacing this lets the operator either
//      consolidate the rules into one trigger or accept the risk
//      knowingly.
//
// The component renders the warning list above the rule list for the
// affected stage so the operator sees it WITHOUT having to drag-reorder
// first.

import type { RuleAttachment, RuleTrigger, RuleType } from '@capture/shared';

/**
 * Canonical runtime-execution order of triggers within a single stage.
 * Lower index = runs earlier. Anything not listed here (defensively) is
 * treated as running last.
 *
 * Keep in lock-step with `TRIGGER_GROUPS` in `RulesTab.tsx`.
 */
const TRIGGER_EXECUTION_ORDER: readonly RuleTrigger[] = [
  'on-step-enter',
  'on-change',
  'on-validate',
  'on-export',
  'on-schedule',
];

/** Rule types that transform/produce field values — should run before consumers. */
const TRANSFORM_TYPES: ReadonlySet<RuleType> = new Set<RuleType>([
  'substitute',
  'format',
  'calculate',
  'copy',
]);

/** Rule types that consume/validate field values — should run after transforms. */
const CONSUMER_TYPES: ReadonlySet<RuleType> = new Set<RuleType>([
  'validate',
  'crossField',
]);

export interface DependencyOrderWarning {
  /** The consumer rule that runs before a transform on the same field. */
  consumerRuleId: string;
  /** The transform rule that runs later. */
  transformRuleId: string;
  /** Human-readable description for the warning panel. */
  message: string;
  /** Stage these rules share. */
  stage: string;
  /** The field both rules reference. */
  fieldRef: string;
}

function triggerIndex(trigger: RuleTrigger): number {
  const i = TRIGGER_EXECUTION_ORDER.indexOf(trigger);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/**
 * Returns the dependency-order warnings for a single stage's rules.
 *
 * Walks rules in canonical runtime order — primary key is trigger
 * execution order, secondary key is `displayOrder` — and emits a
 * warning for the two patterns described in the module header:
 *
 *   1. Same-trigger consumer-before-transform on the same fieldRef.
 *   2. Cross-trigger transform-then-consumer on the same fieldRef
 *      (the canonical stale-values risk).
 *
 * One warning per offending (consumer, transform) pair, deduped on
 * `consumerRuleId` so the panel doesn't shout twice about the same
 * consumer when it precedes multiple later transforms.
 *
 * @param stageRules Rules belonging to a single stage (any triggers).
 */
export function checkStageDependencyOrder(
  stageRules: readonly RuleAttachment[],
  /**
   * R-78c — resolve a rule's `fieldRef` (a stable field-definition id /
   * UUID) to its human label for the warning text. Falls back to the raw
   * `fieldRef` when no resolver is given or it returns undefined. The
   * `fieldRef` on the warning object is left raw so the panel can still
   * link/scroll by id.
   */
  resolveFieldLabel?: (fieldRef: string) => string | undefined,
): DependencyOrderWarning[] {
  const warnings: DependencyOrderWarning[] = [];
  const flaggedConsumerIds = new Set<string>();

  // Group by fieldRef — only rules with a fieldRef participate.
  const byField = new Map<string, RuleAttachment[]>();
  for (const rule of stageRules) {
    if (!rule.fieldRef) continue;
    const list = byField.get(rule.fieldRef) ?? [];
    list.push(rule);
    byField.set(rule.fieldRef, list);
  }

  for (const [fieldRef, fieldRules] of byField) {
    // R-78c — human label for messages; raw ref kept on the warning.
    const fieldName = resolveFieldLabel?.(fieldRef) ?? fieldRef;
    // Sort by (trigger order, displayOrder). Rules that share a trigger
    // keep their relative order; cross-trigger ordering is the whole
    // point of the R-43 fix.
    const ordered = [...fieldRules].sort((a, b) => {
      const ta = triggerIndex(a.trigger);
      const tb = triggerIndex(b.trigger);
      if (ta !== tb) return ta - tb;
      return a.displayOrder - b.displayOrder;
    });

    // PASS 1 — same-trigger consumer-before-transform (legacy pattern).
    // Group rules by trigger and check ordering within each group.
    const byTrigger = new Map<RuleTrigger, RuleAttachment[]>();
    for (const rule of ordered) {
      const list = byTrigger.get(rule.trigger) ?? [];
      list.push(rule);
      byTrigger.set(rule.trigger, list);
    }
    for (const [, triggerRules] of byTrigger) {
      const pending: RuleAttachment[] = [];
      for (const rule of triggerRules) {
        if (CONSUMER_TYPES.has(rule.type)) {
          pending.push(rule);
        } else if (TRANSFORM_TYPES.has(rule.type) && pending.length > 0) {
          for (const consumer of pending) {
            if (flaggedConsumerIds.has(consumer.id)) continue;
            flaggedConsumerIds.add(consumer.id);
            warnings.push({
              consumerRuleId: consumer.id,
              transformRuleId: rule.id,
              stage: rule.stage,
              fieldRef,
              message:
                `"${consumer.type}" on ${consumer.trigger} runs before ` +
                `"${rule.type}" on ${rule.trigger} for field "${fieldName}" — ` +
                `the validator may see stale values. Move the transform ` +
                `earlier (or onto an earlier trigger) so it runs first.`,
            });
          }
          pending.length = 0;
        }
      }
    }

    // PASS 2 — cross-trigger transform-then-consumer (the new R-43
    // pattern). Walk the canonical-order list; for every consumer we
    // hit, look BACKWARD for a transform on an EARLIER trigger for the
    // same field. If found, that's a fragile cross-trigger dependency
    // — the consumer relies on the transform having run, but they're
    // wired to different triggers.
    const transformsSeenByTrigger = new Map<RuleTrigger, RuleAttachment>();
    for (const rule of ordered) {
      if (TRANSFORM_TYPES.has(rule.type)) {
        // Remember the LAST transform per trigger so the message
        // references the rule most directly upstream.
        transformsSeenByTrigger.set(rule.trigger, rule);
      } else if (CONSUMER_TYPES.has(rule.type)) {
        if (flaggedConsumerIds.has(rule.id)) continue;
        // Find the most recent transform on a strictly-earlier trigger.
        const consumerTriggerIdx = triggerIndex(rule.trigger);
        let bestTransform: RuleAttachment | undefined;
        for (const [trigger, transform] of transformsSeenByTrigger) {
          if (triggerIndex(trigger) < consumerTriggerIdx) {
            bestTransform = transform;
          }
        }
        if (bestTransform) {
          flaggedConsumerIds.add(rule.id);
          warnings.push({
            consumerRuleId: rule.id,
            transformRuleId: bestTransform.id,
            stage: rule.stage,
            fieldRef,
            message:
              `"${bestTransform.type}" on ${bestTransform.trigger} ` +
              `transforms field "${fieldName}" before ` +
              `"${rule.type}" on ${rule.trigger} reads it. ` +
              `Cross-trigger ordering is fragile (revalidate-on-load, ` +
              `on-change debouncing). Consolidate both rules onto the ` +
              `same trigger so the order is explicit.`,
          });
        }
      }
    }
  }

  return warnings;
}
